import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Sparkles,
  BookOpen,
  Layers,
  Route,
  Lightbulb,
  ChevronRight,
  MapPin,
  Play,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { usePortalHelp } from '../../context/PortalHelpContext';
import { usePortalTour } from '../../context/PortalTourContext';

const TABS = [
  { id: 'guide', label: 'Guide', icon: BookOpen },
  { id: 'sections', label: 'Sections', icon: Layers },
  { id: 'workflows', label: 'Workflows', icon: Route },
  { id: 'tips', label: 'Tips', icon: Lightbulb },
];

const STORAGE_KEY = 'portal-help-bubble-position';
const BUTTON_SIZE = 56;
const DRAG_THRESHOLD_PX = 8;

function getDefaultBubblePosition() {
  return {
    x: Math.max(8, window.innerWidth - BUTTON_SIZE - 16),
    y: Math.max(8, window.innerHeight - BUTTON_SIZE - 24),
  };
}

function clampBubblePosition(pos) {
  return {
    x: Math.min(Math.max(pos.x, 8), window.innerWidth - BUTTON_SIZE - 8),
    y: Math.min(Math.max(pos.y, 8), window.innerHeight - BUTTON_SIZE - 8),
  };
}

function readSavedBubblePosition() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return clampBubblePosition(parsed);
    }
  } catch {
    // ignore invalid saved position
  }
  return null;
}

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={() => onClick(tab.id)}
      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium rounded-lg transition-colors ${
        active
          ? 'bg-primary text-white shadow-sm'
          : 'text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {tab.label}
    </button>
  );
}

export default function PortalHelpBubble() {
  const {
    pageHelp,
    helpAvailable,
    panelOpen,
    activeTab,
    setActiveTab,
    highlightSectionId,
    toggleHelp,
    closeHelp,
    scrollToSection,
    isAuditor,
    showWhatsNew,
    helpChangelog,
    dismissWhatsNew,
    isFirstVisit,
  } = usePortalHelp();
  const { startTour, TOUR_IDS } = usePortalTour();

  const [position, setPosition] = useState(() => readSavedBubblePosition() || getDefaultBubblePosition());
  const [isDragging, setIsDragging] = useState(false);
  const positionRef = useRef(position);
  const dragRef = useRef(null);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    const onResize = () => {
      setPosition((current) => clampBubblePosition(current));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const persistPosition = useCallback((nextPosition) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextPosition));
    } catch {
      // ignore storage failures
    }
  }, []);

  const handleBubblePointerDown = (event) => {
    if (event.button !== 0) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: positionRef.current.x,
      originY: positionRef.current.y,
      dragging: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleBubblePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;

    if (!drag.dragging) {
      if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) return;
      drag.dragging = true;
      setIsDragging(true);
    }

    const nextPosition = clampBubblePosition({
      x: drag.originX + deltaX,
      y: drag.originY + deltaY,
    });
    setPosition(nextPosition);
  };

  const handleBubblePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    event.currentTarget.releasePointerCapture(event.pointerId);

    if (drag.dragging) {
      persistPosition(positionRef.current);
    } else {
      toggleHelp();
    }

    dragRef.current = null;
    setIsDragging(false);
  };

  const handleBubblePointerCancel = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  if (!helpAvailable || !pageHelp) return null;

  const panelLeft = Math.max(8, Math.min(position.x, window.innerWidth - 420 - 8));
  const panelBottom = Math.max(88, window.innerHeight - position.y + 12);

  const workflows = (pageHelp.workflows || []).filter((w) => {
    if (w.tourId === TOUR_IDS.payroll && isAuditor) return false;
    return true;
  });

  const panel = panelOpen ? (
    <div
      className="fixed z-[9998] w-[min(420px,calc(100vw-2rem))] max-h-[min(640px,78vh)] flex flex-col overflow-hidden rounded-2xl border border-[#E4E8EB]/90 bg-white shadow-[0_24px_64px_rgba(15,92,94,0.2)]"
      style={{ left: panelLeft, bottom: panelBottom }}
      role="dialog"
      aria-label={`Help: ${pageHelp.title}`}
    >
      <div className="relative bg-gradient-to-br from-primary to-primary/85 px-5 pt-5 pb-4 text-white">
        <button
          type="button"
          onClick={closeHelp}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/15 transition-colors"
          aria-label="Close help"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-8">
          <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-white/80">Help on this page</p>
            <h2 className="text-lg font-heading font-semibold leading-tight">{pageHelp.title}</h2>
            <p className="text-sm text-white/90 mt-0.5">{pageHelp.tagline}</p>
          </div>
        </div>
        <div className="flex gap-1 mt-4 p-1 bg-white/10 rounded-xl">
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              active={activeTab === tab.id}
              onClick={setActiveTab}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {showWhatsNew && helpChangelog ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-amber-900">What&apos;s new in help</p>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">{helpChangelog}</p>
            </div>
            <button
              type="button"
              onClick={dismissWhatsNew}
              className="text-xs font-medium text-amber-900 hover:underline flex-shrink-0"
            >
              Got it
            </button>
          </div>
        ) : null}

        {activeTab === 'guide' && (
          <>
            {pageHelp.nextAction ? (
              <Link
                to={pageHelp.nextAction.href}
                onClick={closeHelp}
                className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
              >
                <span>Next: {pageHelp.nextAction.label}</span>
                <ChevronRight className="h-4 w-4" />
              </Link>
            ) : null}

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-primary mb-1.5">Overview</p>
              <p className="text-text-primary leading-relaxed">{pageHelp.overview}</p>
            </div>
            {pageHelp.whenToUse ? (
              <div className="rounded-xl bg-[#F8FAFA] border border-[#E4E8EB] px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1">When to use this page</p>
                <p className="text-text-primary leading-relaxed">{pageHelp.whenToUse}</p>
              </div>
            ) : null}
            {pageHelp.quickSteps?.length ? (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary mb-2">Quick steps</p>
                <ol className="space-y-2">
                  {pageHelp.quickSteps.map((step, i) => (
                    <li key={step} className="flex gap-2.5">
                      <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-text-primary leading-relaxed pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            {pageHelp.relatedLinks?.length ? (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-2">Related pages</p>
                <div className="flex flex-wrap gap-2">
                  {pageHelp.relatedLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={closeHelp}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      {link.label}
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        {activeTab === 'sections' && (
          <>
            {!pageHelp.sections?.length ? (
              <p className="text-text-muted">No section breakdown for this page — use the Guide tab for overview.</p>
            ) : (
              <div className="space-y-3">
                {pageHelp.sections.map((section) => (
                  <div
                    key={section.id}
                    className={`rounded-xl border px-4 py-3 transition-colors ${
                      highlightSectionId === section.id
                        ? 'border-primary bg-primary/5'
                        : 'border-[#E4E8EB] hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-text-primary">{section.title}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-primary px-2"
                        onClick={() => scrollToSection(section.id)}
                      >
                        <MapPin className="h-3 w-3 mr-1" />
                        Show
                      </Button>
                    </div>
                    <p className="text-text-muted text-xs mt-2 leading-relaxed">{section.what}</p>
                    <p className="text-text-primary text-xs mt-2 leading-relaxed">
                      <span className="font-medium text-primary">Do: </span>
                      {section.doThis}
                    </p>
                    {section.mistakes ? (
                      <p className="text-amber-800 text-xs mt-2 leading-relaxed bg-amber-50 rounded-lg px-2 py-1.5">
                        {section.mistakes}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'workflows' && (
          <>
            {!workflows.length ? (
              <p className="text-text-muted">
                No guided workflow on this page.
                {' '}
                <button
                  type="button"
                  className="text-primary font-medium hover:underline"
                  onClick={() => {
                    closeHelp();
                    startTour(TOUR_IDS.overview, { force: true });
                  }}
                >
                  Run full portal tour
                </button>
              </p>
            ) : (
              <div className="space-y-3">
                {workflows.map((wf) => (
                  <div
                    key={wf.id}
                    className="rounded-xl border border-[#E4E8EB] p-4 hover:border-primary/30 transition-colors"
                  >
                    <p className="font-medium text-text-primary">{wf.label}</p>
                    {wf.description ? (
                      <p className="text-text-muted text-xs mt-1 leading-relaxed">{wf.description}</p>
                    ) : null}
                    {wf.tourId ? (
                      <Button
                        type="button"
                        size="sm"
                        className="mt-3 h-8 text-xs"
                        onClick={() => {
                          closeHelp();
                          window.setTimeout(() => startTour(wf.tourId, { force: true }), 150);
                        }}
                      >
                        <Play className="h-3 w-3 mr-1.5" />
                        Start guided tour
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'tips' && (
          <>
            {pageHelp.glossary?.length ? (
              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Key terms</p>
                {pageHelp.glossary.map((item) => (
                  <div key={item.term} className="rounded-lg bg-[#F8FAFA] px-3 py-2.5">
                    <p className="font-medium text-text-primary text-xs">{item.term}</p>
                    <p className="text-text-muted text-xs mt-1 leading-relaxed">{item.definition}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-muted text-sm">No glossary entries on this page.</p>
            )}
            <div className="rounded-xl border border-dashed border-[#E4E8EB] px-4 py-3 text-xs text-text-muted">
              <p className="font-medium text-text-primary mb-1">Need the big picture?</p>
              <p className="leading-relaxed">
                Header Help → Run portal tour, or open Workflows here for step-by-step pay and billing paths.
              </p>
            </div>
          </>
        )}
      </div>

      <div className="border-t border-[#E4E8EB] px-5 py-3 bg-[#F8FAFA] flex items-center justify-between gap-2">
        <p className="text-[10px] text-text-muted">Updates per page · Look for ? icons on cards</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            closeHelp();
            startTour(TOUR_IDS.overview, { force: true });
          }}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Full tour
        </Button>
      </div>
    </div>
  ) : null;

  return createPortal(
    <>
      {panel}
      <button
        type="button"
        onPointerDown={handleBubblePointerDown}
        onPointerMove={handleBubblePointerMove}
        onPointerUp={handleBubblePointerUp}
        onPointerCancel={handleBubblePointerCancel}
        style={{ left: position.x, top: position.y }}
        className={`fixed z-[9997] h-14 w-14 rounded-full shadow-[0_8px_32px_rgba(15,92,94,0.35)] flex items-center justify-center transition-shadow duration-200 touch-none select-none ${
          isDragging ? 'cursor-grabbing scale-95' : 'cursor-grab'
        } ${
          panelOpen
            ? 'bg-slate-700 text-white'
            : 'bg-primary text-white hover:shadow-[0_12px_40px_rgba(15,92,94,0.45)]'
        }`}
        aria-label={panelOpen ? 'Close help panel (drag to move)' : 'Open help for this page (drag to move)'}
        aria-expanded={panelOpen}
        title="Drag to move · Click to open help"
        data-tour="portal-help-bubble"
      >
        {panelOpen ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
        {!panelOpen && (isFirstVisit || showWhatsNew) ? (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-400 border-2 border-white animate-pulse" aria-hidden />
        ) : null}
      </button>
      <style>{`
        [data-help-highlight="true"] {
          outline: 2px solid rgba(15, 92, 94, 0.55);
          outline-offset: 3px;
          border-radius: 12px;
          transition: outline 0.2s ease;
        }
      `}</style>
    </>,
    document.body,
  );
}